import classNames from "classnames";

import { goalTypeLabel, type TemplateVariable } from "@/shared/domain/agents";
import { Card } from "@/shared/foundation/primitives";
import textStyles from "@/shared/foundation/styles/textStyles.module.css";

import { GOAL_KIND_COPY } from "../../AgentForm/goals/goalKinds";
import { KeyValueTable } from "../../AgentForm/KeyValueTable";
import {
  type GoalState,
  type PassthroughGoal,
} from "../../Agents/buildRequestTemplate";
import {
  describeGoal,
  type GoalArtifact,
  goalArtifacts,
} from "../describeGoal";
import { highlightVariables } from "../highlightVariables";

import { SectionEditLink } from "./SectionEditLink";
import styles from "./sections.module.css";

export interface GoalsSectionProps {
  agentId: string;
  goals: GoalState[];
  /**
   * Saved goals of a type the editor cannot edit, such as one added to the
   * SDK after this build. Listed by name only, after the editable goals.
   */
  passthroughGoals?: PassthroughGoal[];
  /** The message seeded before the agent's first turn; empty when none. */
  kickoffMessage: string;
  /** Every variable the agent declares. */
  variables: TemplateVariable[];
}

/**
 * What the agent works on: its goals, the kickoff message it is started with,
 * and the variables it declares, in the table the form lists them in. An
 * agent with no goals lists none: its kickoff message is what it works on.
 */
export function GoalsSection({
  agentId,
  goals,
  passthroughGoals = [],
  kickoffMessage,
  variables,
}: GoalsSectionProps) {
  const hasKickoff = kickoffMessage.trim() !== "";
  const goalCount = goals.length + passthroughGoals.length;

  return (
    <Card.Root
      size="section"
      data-testid="agent-detail-goals"
      data-count={goalCount}
    >
      <Card.Header>
        <Card.Title>Goals</Card.Title>
        <Card.Actions>
          <SectionEditLink agentId={agentId} section="goals" label="goals" />
        </Card.Actions>
      </Card.Header>
      <Card.Body>
        <div className={styles.groups}>
          {goalCount > 0 && (
            <ul className={styles.rows}>
              {goals.map((goal, index) => (
                <li
                  key={index}
                  className={styles.row}
                  data-testid="agent-detail-goal"
                  data-kind={goal.goal_type}
                >
                  <div className={styles.rowText}>
                    <h3
                      className={classNames(
                        styles.rowTitle,
                        styles.blockHeading,
                        styles.goalTitle,
                      )}
                    >
                      <GoalKindIcon kind={goal.goal_type} />
                      {GOAL_KIND_COPY[goal.goal_type].label}
                    </h3>
                    <p className={styles.rowSummary}>{describeGoal(goal)}</p>
                    <div className={styles.goalArtifacts}>
                      {goalArtifacts(goal).map((artifact) => (
                        <GoalArtifactBlock
                          key={artifact.id}
                          artifact={artifact}
                        />
                      ))}
                    </div>
                  </div>
                </li>
              ))}
              {passthroughGoals.map(({ savedIndex, goal }) => (
                <li
                  key={`passthrough-${savedIndex}`}
                  className={styles.row}
                  data-testid="agent-detail-goal"
                  data-kind={String(goal.goal_type)}
                >
                  <div className={styles.rowText}>
                    <h3
                      className={classNames(
                        styles.rowTitle,
                        styles.blockHeading,
                        styles.goalTitle,
                      )}
                    >
                      {goalTypeLabel(String(goal.goal_type))}
                    </h3>
                    <p className={styles.rowSummary}>
                      Set through the SDK or API. The editor cannot change it.
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <section className={styles.block}>
            <h3
              className={classNames(textStyles.overline, styles.blockHeading)}
            >
              Kickoff message
            </h3>
            {hasKickoff ? (
              <blockquote
                className={styles.quote}
                data-testid="agent-detail-kickoff"
              >
                {highlightVariables(kickoffMessage)}
              </blockquote>
            ) : (
              <p
                className={styles.muted}
                data-testid="agent-detail-kickoff-none"
              >
                None
              </p>
            )}
          </section>

          <section className={styles.block}>
            <h3
              className={classNames(textStyles.overline, styles.blockHeading)}
            >
              Variables
            </h3>
            <KeyValueTable
              keyHeading="Variable"
              valueHeading="Description"
              data-testid="agent-detail-variables"
              data-count={variables.length}
            >
              {variables.length === 0 ? (
                <KeyValueTable.EmptyRow data-testid="agent-detail-variables-empty">
                  No variables. Naming one in a goal or the kickoff message
                  declares it.
                </KeyValueTable.EmptyRow>
              ) : (
                variables.map((variable) => (
                  <KeyValueTable.Row
                    key={variable.name}
                    label={variable.name}
                    labelClassName={styles.variableName}
                    data-testid={`agent-detail-variable-${variable.name}`}
                  >
                    {variable.description ? (
                      variable.description
                    ) : (
                      <span className={styles.muted}>No description</span>
                    )}
                  </KeyValueTable.Row>
                ))
              )}
            </KeyValueTable>
          </section>
        </div>
      </Card.Body>
    </Card.Root>
  );
}

type TableArtifactId = Extract<GoalArtifact, { kind: "table" }>["id"];

/** Each vocabulary's words, as the form's goal cards name them. */
const TABLE_COPY: Record<
  TableArtifactId,
  { heading: string; keyHeading: string; valueHeading: string; none: string }
> = {
  label_vocabulary: {
    heading: "Label vocabulary",
    keyHeading: "Label",
    valueHeading: "When it applies",
    none: "No labels set.",
  },
  event_vocabulary: {
    heading: "Event types",
    keyHeading: "Event type",
    valueHeading: "What it captures",
    none: "No event types set.",
  },
  tag_vocabulary: {
    heading: "Tags",
    keyHeading: "Tag",
    valueHeading: "When it applies",
    none: "No tags set.",
  },
};

const PROMPT_HEADINGS = {
  summary_format: "Summary format hint",
  event_focus: "Event focus",
} as const;

/** The glyph the form's picker gives this kind of goal, beside its title here. */
function GoalKindIcon({ kind }: { kind: GoalState["goal_type"] }) {
  const Icon = GOAL_KIND_COPY[kind].icon;
  return <Icon aria-hidden size={16} className={styles.goalIcon} />;
}

function GoalArtifactBlock({ artifact }: { artifact: GoalArtifact }) {
  const testProps = {
    "data-testid": "agent-detail-goal-artifact",
    "data-artifact": artifact.id,
  };

  if (artifact.id === "collection") {
    return (
      <p className={styles.muted} {...testProps}>
        Created events are filed into a collection chosen at launch.
      </p>
    );
  }

  if (artifact.kind === "table") {
    const copy = TABLE_COPY[artifact.id];
    return (
      <section
        className={styles.block}
        {...testProps}
        data-count={artifact.rows.length}
      >
        <h4 className={classNames(textStyles.overline, styles.blockHeading)}>
          {copy.heading}
        </h4>
        <KeyValueTable
          keyHeading={copy.keyHeading}
          valueHeading={copy.valueHeading}
        >
          {artifact.rows.length === 0 ? (
            <KeyValueTable.EmptyRow data-testid="agent-detail-goal-artifact-empty">
              {copy.none}
            </KeyValueTable.EmptyRow>
          ) : (
            artifact.rows.map((row) => (
              <KeyValueTable.Row key={row.key} label={row.key}>
                {row.description ? (
                  highlightVariables(row.description)
                ) : (
                  <span className={styles.muted}>No description</span>
                )}
              </KeyValueTable.Row>
            ))
          )}
        </KeyValueTable>
      </section>
    );
  }

  return (
    <section className={styles.block} {...testProps}>
      <h4 className={classNames(textStyles.overline, styles.blockHeading)}>
        {PROMPT_HEADINGS[artifact.id]}
      </h4>
      {artifact.kind === "quote" ? (
        <blockquote className={styles.quote}>
          {highlightVariables(artifact.text)}
        </blockquote>
      ) : (
        <p className={styles.muted}>Default format.</p>
      )}
    </section>
  );
}
